import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';

const COLORS = {
  success: {
    start: '#2E7D32',
    end: '#1B5E20',
    light: '#E8F5E9',
    border: '#C8E6C9',
    button: '#2E7D32',
    buttonHover: '#388E3C',
  },
  error: {
    start: '#D32F2F',
    end: '#B71C1C',
    light: '#FFEBEE',
    border: '#FFCDD2',
    button: '#D32F2F',
    buttonHover: '#C62828',
  },
  warning: {
    start: '#65C8D0',
    end: '#3DADB5',
    light: '#E0F6F8',
    border: '#4BBBC4',
    button: '#3DADB5',
    buttonHover: '#65C8D0',
  },
  info: {
    start: '#1976D2',
    end: '#1565C0',
    light: '#E3F2FD',
    border: '#90CAF9',
    button: '#1976D2',
    buttonHover: '#1565C0',
  },
};

function AlertDialog({
  open,
  severity,
  title,
  message,
  onClose,
  onConfirm,
  onCancel,
  okText,
  cancelText,
}) {
  const theme = COLORS[severity] || COLORS.info;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.start} 0%, ${theme.end} 100%)`,
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.95rem',
          }}
        >
          {title}
        </Typography>

        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: '#fff' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Body */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
        }}
      >
        {severity === 'warning' ? (
          <>
            <Typography
              sx={{
                fontSize: '0.82rem',
                color: '#555',
                mb: 1.5,
              }}
            >
              The following warnings were detected. Do you still want to save?
            </Typography>

            <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
              {message}
            </Alert>
          </>
        ) : (
          <Alert severity={severity} sx={{ fontSize: '0.85rem' }}>
            {message}
          </Alert>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: `1px solid ${theme.light}`,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
        }}
      >
        {cancelText && (
          <Button
            variant="outlined"
            onClick={onCancel || onClose}
            sx={{
              borderColor: theme.border,
              color: theme.button,
              '&:hover': {
                borderColor: theme.button,
                backgroundColor: theme.light,
              },
            }}
          >
            {cancelText}
          </Button>
        )}

        <Button
          variant="contained"
          onClick={onConfirm || onClose}
          sx={{
            backgroundColor: theme.button,
            fontWeight: 700,
            px: 3,
            '&:hover': {
              backgroundColor: theme.buttonHover,
            },
          }}
        >
          {okText}
        </Button>
      </Box>
    </Dialog>
  );
}

AlertDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  severity: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  title: PropTypes.string,
  message: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  okText: PropTypes.string,
  cancelText: PropTypes.string,
};

AlertDialog.defaultProps = {
  severity: 'info',
  title: 'Message',
  message: '',
  okText: 'OK',
  cancelText: null,
  onConfirm: null,
  onCancel: null,
};

export default AlertDialog;